import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {DatePipe, formatDate} from '@angular/common';
import {saveAs} from 'file-saver';
import JSZip from 'jszip';
import {QedSymbolsInfo} from '../models/qedSymbolsInfo';
import {QedSymbol} from '../models/qedSymbol';
import {QedMulticast} from '../models/qedMulticast';

@Injectable()
export class SymbolsService {

  constructor(private httpClient: HttpClient, public datePipe: DatePipe) {
  }

  getQedSymbolInfoWithStartDate(startDate, service): Observable<QedSymbolsInfo[]> {
    return this.httpClient.get<QedSymbolsInfo[]>(environment.baseUrlWs +
      '/api/qed_symbols_info?service=' + service + '&startDate=' + startDate);
  }

  getSymbolInfoBetweenDates(nextWeek, service = 'qed'): Observable<QedSymbolsInfo[]> {
    return this.httpClient.get<QedSymbolsInfo[]>(environment.baseUrlWs + '/api/symbols-info?service=' + service + '&next-week=' + nextWeek);
  }

  b64toBlob(b64Data, contentType, sliceSize) {
    contentType = contentType || 'application/pdf';
    sliceSize = sliceSize || 512;

    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, {type: contentType});
  }

  getSymbolList(startDate: string, service = 'qed', qhfOnly = false): Observable<QedSymbol[]> {
    const qhfOnlyParam = qhfOnly ? '&qhfOnly=1' : '';
    return this.httpClient.get<QedSymbol[]>(environment.baseUrlWs
      + '/api/qed_symbols?service=' + service + '&startDate=' + startDate + qhfOnlyParam);
  }

  getMulticastList(service: string, startDate: string, newsOnly = false, qhfOnly = false): Observable<QedMulticast[]> {
    const newsOnlyParam = newsOnly ? '&newsOnly=1' : '';
    const qhfOnlyParam = qhfOnly ? '&qhfOnly=1' : '';
    return this.httpClient.get<QedMulticast[]>(environment.baseUrlWs
      + '/api/qed_multicast?service=' + service + '&startDate=' + encodeURIComponent(startDate) + newsOnlyParam + qhfOnlyParam);
  }

  getMulticastDetailsList(service: string, startDate: string): Observable<QedMulticast[]> {
    return this.httpClient.get<QedMulticast[]>(environment.baseUrlWs
      + '/api/qed-multicast-details?service=' + service + '&startDate=' + encodeURIComponent(startDate));
  }

  getQrdSymbolInfo(): Observable<QedSymbolsInfo[]> {
    return this.httpClient.get<QedSymbolsInfo[]>(environment.baseUrlWs +
      '/api/qed_symbols_info?service=qrd&endDate[null]=true');
  }

  getSymbolStartDates(service: string): Observable<string[]> {
    return this.httpClient.get<string[]>(environment.baseUrlWs +
      '/api/symbol-list-start-dates?service=' + service);
  }

  getClientSymbolsList(service: string, includingInstrumentId): Observable<any> {
    return this.httpClient.get(environment.baseUrlWs + '/api/symbol-list?service=' + service + '&include_instrument_id=' + includingInstrumentId + '&ui=1');
  }

  getNextWeekClientSymbolsList(service: string): Observable<any> {
    return this.httpClient.get(environment.baseUrlWs + '/api/symbol-list?service=' + service + '&ui=1&next-week=1');
  }

  getClientSymbolsListByDate(startDateAsString: string, endDateAsString: string, service: string): Observable<any> {
    return this.httpClient.get(environment.baseUrlWs +
      '/api/symbol-list-by-dates?service=' + service + '&start_date=' + startDateAsString + '&end_date=' + endDateAsString);
  }

  getNumberOfWeek(selectedDate) {
    let defaultDate = new Date();
    if (selectedDate) {
      defaultDate = new Date(selectedDate);
    }
    const years = defaultDate.getFullYear();
    const months = defaultDate.getMonth();
    const days = defaultDate.getDate();
    let weekNum = 0;
    const monthList = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (years % 4 === 0 && years % 100 !== 0 || years % 400 === 0) {
      monthList[1] = 29;
    }
    let totalDays = 0;
    for (let cpt = 0; cpt < months; cpt++) {
      totalDays += monthList[cpt];
    }
    totalDays += days;
    const firstJanuary = new Date(years, 0, 1);
    let firstDayInYear = firstJanuary.getDay();
    if (firstDayInYear === 0) {
      firstDayInYear = 7;
    }
    totalDays -= 8 - firstDayInYear;
    weekNum = 1;
    weekNum += Math.floor(totalDays / 7);
    if (totalDays % 7 !== 0) {
      weekNum += 1;
    }
    return weekNum;
  }

  getNumberOfWeekAndSelectedDate(selectedDate) {
    if (!selectedDate) {
      const today = new Date();
      return this.getNumberOfWeek(null) + ' - ' + formatDate(today, 'dd MMM yyyy', 'en-US');
    }
    const numberOfWeeksOfSelectedDate = this.getNumberOfWeek(selectedDate);
    return numberOfWeeksOfSelectedDate + '-' + formatDate(selectedDate, 'dd-MMM-yyyy', 'en-US');
  }

  getQrdSymbol(newsOnly = false): Observable<QedSymbol[]> {
    const newsOnlyParam = newsOnly ? '&newsOnly=1' : '';
    return this.httpClient.get<QedSymbol[]>(environment.baseUrlWs +
      '/api/qed_symbols?service=qrd&endDate[null]=true' + newsOnlyParam);
  }

  addCsvFilesToZipFileWhenDateRangeSelected(pastDateRange1Form, pastDateRange2Form, listQedSymbols, service, mcast = false) {
    // tslint:disable-next-line:max-line-length
    let blobType: BlobPart;
    blobType = this.initializeBlobType(service, mcast);
    const blocLine = [];
    const zip = new JSZip();
    const time_diff = new Date(pastDateRange2Form.value).getTime() - new Date(pastDateRange1Form.value).getTime();
    const days_Diff = Math.abs(time_diff / (1000 * 3600 * 24));
    const date = new Date(pastDateRange1Form.value);
    for (let j = 0; j <= days_Diff; j++) {
      this.createCsvFile(listQedSymbols, blocLine, blobType, zip, date, service, mcast);
      blobType = this.initializeBlobType(service, mcast);
      date.setDate(date.getDate() + 1);
    }
    zip.generateAsync({type: 'blob'}).then(function (content) {
      saveAs(content, 'symbol-list-history.zip');
    });
  }

  initializeBlobType(service, mcast) {
    if (service === 'QED') {
      if (!mcast) {
        return 'Market, QED product, Channel, Asset class, Product, Name, Maturity, Multicast address, QED symbol\n';
      }
      return 'POP, Channel, Non-Encrypted Protected Prod, Non-Encrypted Protected UAT, Non-Encrypted Non-Protected Prod,' +
        'Non-Encrypted Non-Protected UAT, Encrypted Protected Prod, Encrypted Protected UAT,' +
        'Encrypted Non-Protected Prod, Encrypted Non-Protected UAT, Non-Encrypted Protected Prod Port, Non-Encrypted Protected UAT Port,' +
        'Non-Encrypted Non-Protected Prod Port, Non-Encrypted Non-Protected UAT Port, Encrypted Protected Prod Port,' +
        'Encrypted Protected UAT Port, Encrypted Non-Protected Prod Port, Encrypted Non-Protected UAT Port\n';
    } else if (service === 'QRD') {
      if (!mcast) {
        return 'Market, QRD/QPD product, Channel, Currently distributed, Description\n';
      } else {
        return 'Pop Name, Channel, Raw QRD IP / Port, Raw QPD IP / Port\n';
      }
    }
    return '';
  }

  getFileName(selectedDate, service, mcast) {
    let filename: string;
    if (service === 'QED') {
      if (!mcast) {
        filename = 'qed-symbol-list-week-';
      } else {
        filename = 'qed-multicast-list-week-';
      }
    } else if (service === 'QRD') {
      if (!mcast) {
        filename = 'qrd-symbol-list-week-';
      } else {
        filename = 'qrd-multicast-list-week-';
      }
    }
    return filename + this.getNumberOfWeekAndSelectedDate(selectedDate);
  }

  verifyDateSelected(rangeDate1 = false, rangeDate2 = false, pastDateRange1Form, pastDateRange2Form) {
    if (rangeDate1 && !!pastDateRange2Form.value && pastDateRange1Form.value > pastDateRange2Form.value) {
      pastDateRange1Form.setValue('');
    }
    if (rangeDate2 && !!pastDateRange1Form.value && pastDateRange2Form.value < pastDateRange1Form.value) {
      pastDateRange2Form.setValue('');
    }
  }

  private createCsvFile(
    filteredQedSymbols: any[],
    blocLine: any[],
    blobType: ArrayBufferView | ArrayBuffer | Blob | string,
    zip,
    date,
    service,
    mcast) {
    if (service === 'QED') {
      for (const qed of filteredQedSymbols) {
        if (!mcast) {
          blocLine = [qed.marketName, qed.qedProduct, qed.market, qed.assetClass,
            qed.product, qed.name, qed.maturity, qed.multicastAdress, qed.qedSymbol];
        } else {
          blocLine = [qed.pop, qed.channel, qed.nonCryptProtProd, qed.nonCryptProtUat, qed.nonCryptNonProtProd,
            qed.nonCryptNonProtUat, qed.cryptProtProd, qed.cryptProtUat, qed.cryptNonProtProd, qed.cryptNonProtUat,
            qed.nonCryptProtProdPort, qed.nonCryptProtUatPort, qed.nonCryptNonProtProdPort,
            qed.nonCryptNonProtUatPort, qed.cryptProtProdPort, qed.cryptProtUatPort, qed.cryptNonProtProdPort, qed.cryptNonProtUatPort];
        }
        blobType = blobType + blocLine.join(';').replace(',', ':') + '\n';
        blocLine = [];
      }
    } else if (service === 'QRD') {
      for (const qrd of filteredQedSymbols) {
        if (!mcast) {
          blocLine = [qrd.marketName, qrd.qedProduct, qrd.market, qrd.type, qrd.name];
        } else {
          blocLine = [qrd.pop, qrd.qedChannel.name, qrd.rawQrdIpPort, qrd.rawQpdIpPort];
        }
        blobType = blobType + blocLine.join(',') + '\n';
        blocLine = [];
      }
    }
    const csvSpsDatabaseFileToDownloadBlobSample = new Blob([blobType], {
      type: 'text/csv'
    });
    zip.file(this.getFileName(date, service, mcast) + '.csv', csvSpsDatabaseFileToDownloadBlobSample);
  }
}
